import { useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { getAttribution } from "@/lib/attribution";

import { FIRM } from "@/lib/firm";
import { submitEnquiry } from "@/lib/enquiries.functions";

const field =
  "w-full rounded-sm border border-input bg-card px-3 py-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/20";
const labelCls = "block text-sm font-medium text-foreground";

export function ChecklistDownloadGate({
  href,
  label,
  note,
  guideTitle,
}: {
  href: string;
  label: string;
  note: string;
  guideTitle: string;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [sending, setSending] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);
    const name = String(fd.get("name") ?? "").trim();
    const email = String(fd.get("email") ?? "").trim();
    const phone = String(fd.get("phone") ?? "").trim();

    const nextErrors: { name?: string; email?: string } = {};
    if (name.length < 2) nextErrors.name = "Please tell us your name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))
      nextErrors.email = "Please enter a valid email address";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSending(true);
    try {
      await submitEnquiry({
        data: {
          name,
          email,
          phone,
          matterType: "wills-estates",
          propertyAddress: "",
          message: `Checklist download request: ${label} (from guide: ${guideTitle}).`,
          ...getAttribution(pathname),
        } as never,
      });
      setUnlocked(true);
      toast.success("Thanks — your checklist is ready to download.");
    } catch {
      toast.error(`Something went wrong. Please call us on ${FIRM.phone}.`);
    } finally {
      setSending(false);
    }
  }

  return (
    <aside className="mb-12 rounded-sm border border-border bg-secondary/50 p-6">
      <p className="text-xs font-semibold uppercase tracking-wider text-accent">Free download</p>
      <h2 className="mt-2 font-serif text-xl text-foreground">{label}</h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{note}</p>

      {unlocked ? (
        <div className="mt-5">
          <a
            href={href}
            download
            className="inline-flex items-center gap-2 rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            Download the checklist (PDF)
          </a>
          <p className="mt-3 text-xs text-muted-foreground">
            If the download does not start, right-click the button and choose “Save link as”.
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} noValidate className="mt-5 space-y-4">
          <p className="text-xs text-muted-foreground">
            Tell us where to send it and the PDF unlocks straight away.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="gate-name" className={labelCls}>
                Name
              </label>
              <input id="gate-name" name="name" maxLength={120} className={`mt-1.5 ${field}`} />
              {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor="gate-email" className={labelCls}>
                Email
              </label>
              <input
                id="gate-email"
                name="email"
                type="email"
                maxLength={200}
                className={`mt-1.5 ${field}`}
              />
              {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
            </div>
          </div>
          <div>
            <label htmlFor="gate-phone" className={labelCls}>
              Phone <span className="text-muted-foreground">(optional)</span>
            </label>
            <input id="gate-phone" name="phone" maxLength={40} className={`mt-1.5 ${field}`} />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="inline-flex items-center gap-2 rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {sending ? "Preparing your download…" : "Get the checklist (PDF)"}
          </button>
          <p className="text-xs leading-relaxed text-muted-foreground">
            We use your details only to send the checklist and, if you ask, to arrange a
            consultation. No obligation, and we do not share your details.
          </p>
        </form>
      )}
    </aside>
  );
}
